import React, { useState, useEffect, useCallback, useRef } from "react";
import { Bell, Search, Command as CmdIcon, Siren, Settings, User, Truck, UserPlus, Users, AlertCircle, Check, Activity, RefreshCw, Menu, KeyRound, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { ROLE_META } from "@/data/sidebars";
import { useAlert } from "@/hooks/useAlert";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import useGlobalSearch from "@/hooks/useGlobalSearch";
import { resolveSearchNavigation } from "@/lib/searchNavigation";
import VehicleProfileDialog from "./VehicleProfileDialog";
// Command primitives are not required here; TopBar uses a single external input
// and renders results directly inside the Popover.
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useIncidentSocket } from "@/hooks/useIncidentSocket";
import { buildIncidentAlert, isAlertHandledByMyOrg } from "@/lib/incidentAlert";

// ── API type → icon bucket ─────────────────────────────────────────────────
function toDisplayType(apiType) {
  const t = String(apiType || "").toUpperCase();
  if (t === "INCIDENT_CREATED") return "alert";
  if (t === "INCIDENT_STATUS_UPDATED") return "update";
  if (t === "INCIDENT_SERVICE_RESPONSE") return "alert";
  return "update";
}

function relativeTime(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function notifSubtitle(n) {
  const m = n.metadata || {};
  if (n.api_type === "INCIDENT_CREATED") return m.vehicle_plate || null;
  if (n.api_type === "INCIDENT_STATUS_UPDATED")
    return [m.vehicle_plate, m.new_status].filter(Boolean).join(" · ") || null;
  if (n.api_type === "INCIDENT_SERVICE_RESPONSE")
    return [m.org_name, m.response_status].filter(Boolean).join(" · ") || null;
  return null;
}

function getRoleRouteMap(role) {
  const routes = {
    super_admin: {
      alerts: "/admin/alerts",
      active: "/admin/live",
      completed: "/admin/alerts",
    },
    police: {
      alerts: "/police/alerts",
      active: "/police/active",
      completed: "/police/completed",
    },
    ambulance: {
      alerts: "/ambulance/alerts",
      active: "/ambulance/active",
      completed: "/ambulance/completed",
    },
    fire: {
      alerts: "/fire/alerts",
      active: "/fire/active",
      completed: null,
    },
    hospital: {
      alerts: "/hospital",
      active: "/hospital/active",
      completed: null,
    },
  };

  return routes[role] || {
    alerts: "/login",
    active: "/login",
    completed: null,
  };
}

export default function TopBar({ role, onMenuClick }) {
  const { user, updateMyPassword } = useAuth();
  const meta = ROLE_META[role];
  const { triggerAlert } = useAlert();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedVehiclePlate, setSelectedVehiclePlate] = useState(null);
  const [bellOpen, setBellOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifsLoading, setNotifsLoading] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [mustChangePromptShown, setMustChangePromptShown] = useState(false);
  const shownIncidentRef = useRef(new Set());
  const totalUnreadCount = unreadCount;
  const roleRoutes = getRoleRouteMap(role);
  const searchDashboard = role === "super_admin" ? "admin" : role;

  // ── Fetch unread count (badge) ─────────────────────────────────────────
  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnreadCount(typeof data?.count === "number" ? data.count : 0);
    } catch { /* silent */ }
  }, []);

  // ── Fetch notification list (popover) ─────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    setNotifsLoading(true);
    try {
      const { data } = await api.get("/notifications", {
        params: { is_read: false, page: 1, page_size: 20 },
      });
      const rows = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : (data?.results ?? []));
      setNotifications(
        rows.map((n) => ({
          id: n.id,
          api_type: n.type,
          type: toDisplayType(n.type),
          title: n.title || n.body || "Notification",
          body: n.body || null,
          is_read: !!n.is_read,
          target_id: n.target_id || null,
          metadata: n.metadata || {},
          time: relativeTime(n.created_at),
        }))
      );
    } catch { /* keep existing */ }
    finally { setNotifsLoading(false); }
  }, []);

  // ── Mark single as read ──────────────────────────────────────────────
  const markRead = useCallback(async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      await fetchUnreadCount();
    } catch { /* silent */ }
  }, [fetchUnreadCount]);

  // ── Mark all as read ──────────────────────────────────────────────────
  const markAllRead = useCallback(async () => {
    try {
      await api.patch("/notifications/read-all");
      setNotifications([]);
      await fetchUnreadCount();
    } catch { /* silent */ }
  }, [fetchUnreadCount]);

  // Fetch count on mount
  useEffect(() => { fetchUnreadCount(); }, [fetchUnreadCount]);

  useIncidentSocket((payload) => {
    // Refresh badge
    fetchUnreadCount();

    // If notification popover is open,
    // refresh the list too.
    if (bellOpen) {
      fetchNotifications();
    }

    const incidentId = payload?.incident_id || payload?.id;
    if (!incidentId || shownIncidentRef.current.has(String(incidentId))) return;

    shownIncidentRef.current.add(String(incidentId));

    // WS payloads are minimal: fetch the full incident so plate/location/driver are populated.
    buildIncidentAlert(payload)
      .then((alert) => {
        if (isAlertHandledByMyOrg(alert)) return;
        triggerAlert(alert);
      })
      .catch((err) => console.error("[TopBar] Failed to build incident alert:", err));
  });

  // Fetch list whenever popover opens
  useEffect(() => {
    if (!bellOpen) return;
    fetchNotifications();
  }, [bellOpen, fetchNotifications]);

  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen((s) => !s);
      }
      if (e.key === "Escape") {
        setSearchOpen(false);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Global search hook
  const { query, setQuery, results, loading: searchLoading, error: searchError } = useGlobalSearch();
  const searchRef = React.useRef(null);
  const searchInputRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const itemRefs = useRef([]);

  const flatItems = React.useMemo(() => {
    const groups = [];
    if (results?.vehicles?.length) groups.push({ title: "Vehicles", items: results.vehicles });
    if (results?.incidents?.length) groups.push({ title: "Incidents", items: results.incidents });
    if (results?.users?.length) groups.push({ title: "Users", items: results.users });
    if (results?.organizations?.length) groups.push({ title: "Organizations", items: results.organizations });
    const flat = [];
    groups.forEach((g) => {
      g.items.forEach((it) => flat.push({ ...it, __group: g.title }));
    });
    return { groups, flat };
  }, [results]);

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, flatItems.flat.length);
    setActiveIndex(flatItems.flat.length > 0 ? 0 : -1);
  }, [flatItems.flat.length]);

  // Focus the input when the popover opens so clicks into the trigger allow typing immediately
  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => {
        try { searchInputRef.current?.focus?.(); } catch (e) { /* noop */ }
      }, 0);
    }
  }, [searchOpen]);

  const focusItem = (idx) => {
    const ref = itemRefs.current[idx];
    if (ref && ref.scrollIntoView) ref.scrollIntoView({ block: "nearest" });
  };

  const handleInputKeyDown = (e) => {
    if (!flatItems.flat.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => {
        const next = Math.min((i === -1 ? 0 : i + 1), flatItems.flat.length - 1);
        setTimeout(() => focusItem(next), 0);
        return next;
      });
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => {
        const prev = Math.max((i === -1 ? flatItems.flat.length - 1 : i - 1), 0);
        setTimeout(() => focusItem(prev), 0);
        return prev;
      });
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const item = flatItems.flat[activeIndex];
      if (item) {
        const resolved = resolveSearchNavigation(item.navigation, searchDashboard);
        navigate(resolved.path, resolved.state ? { state: resolved.state } : undefined);
        setSearchOpen(false);
      }
    }
    if (e.key === "Escape") {
      setSearchOpen(false);
    }
  };

  useEffect(() => {
    if (user?.must_change_password && !mustChangePromptShown) {
      setPasswordDialogOpen(true);
      setMustChangePromptShown(true);
      toast.warning("Temporary password detected. Please set a new password.");
    }
  }, [user?.must_change_password, mustChangePromptShown]);

  const handleTestAlert = () => {
    // Manual QA trigger; real incident overlays also come from WebSocket messages.
    triggerAlert({
      type: "High-Speed Collision Detected",
      location: "Gondal Road Highway, Rajkot",
      details: [
        { label: "Confidence", value: "98% (Multi-sensor)" },
        { label: "Vehicles", value: "2 Confirmed" },
        { label: "Hazmat", value: "None Detected" },
        { label: "ETA", value: "Calculate Route..." }
      ]
    });
  };

  const handleOpenProfile = (plate) => {
    setSelectedVehiclePlate(plate);
    setProfileOpen(true);
    setOpen(false);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification?.is_read) {
      await markRead(notification.id);
    }
    navigate(roleRoutes.alerts, {
      state: notification?.target_id ? { selectedId: notification.target_id } : undefined,
    });
    setBellOpen(false);
  };

  const handlePasswordDialogChange = (openValue) => {
    // Advisory only: allow close, but keep reminding after next login if still flagged by API.
    if (!openValue) {
      setNewPassword("");
      setConfirmPassword("");
      setPasswordError("");
    }
    setPasswordDialogOpen(openValue);
  };

  const handleUpdatePassword = async () => {
    const trimmedPassword = newPassword.trim();

    if (!trimmedPassword) {
      setPasswordError("New password is required.");
      return;
    }
    if (trimmedPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters.");
      return;
    }
    if (trimmedPassword !== confirmPassword.trim()) {
      setPasswordError("Passwords do not match.");
      return;
    }

    setPasswordSubmitting(true);
    setPasswordError("");
    try {
      await updateMyPassword(trimmedPassword);
      toast.success("Password updated successfully.");
      handlePasswordDialogChange(false);
    } catch (err) {
      const { message } = extractApiError(err);
      setPasswordError(message || "Failed to update password.");
    } finally {
      setPasswordSubmitting(false);
    }
  };

  return (
    <>
    <header
      data-testid={`topbar-${role}`}
      className="sticky top-0 z-30 flex items-center justify-between border-b border-stone-200 bg-white/90 px-6 py-3 backdrop-blur-md shadow-sm"
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onMenuClick}
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-stone-200 bg-white text-slate-600 transition hover:bg-stone-100 md:hidden"
          aria-label="Open sidebar menu"
        >
          <Menu size={16} />
        </button>
        <span
          className="rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest"
          style={{
            background: "hsl(var(--role-bg-soft))",
            color: "hsl(var(--role-primary))",
          }}
        >
          {meta?.badge}
        </span>
        <span className="hidden text-sm text-slate-500 md:inline">
          {user?.org_name || "Organization"} · {user?.role || "Operator"}
        </span>
      </div>

      <div className="flex items-center gap-3">
        {/* <button
          onClick={handleTestAlert}
          className="hidden md:flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-100 px-3 py-1.5 text-xs font-bold text-slate-700 transition hover:bg-stone-200"
        >
          <Siren size={13} /> Test Alert
        </button> */}

        <Popover open={searchOpen} onOpenChange={setSearchOpen}>
          <PopoverAnchor asChild>
            <div
              ref={searchRef}
              className="hidden items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-1.5 text-xs text-slate-400 transition hover:bg-stone-100 md:flex"
              onMouseDown={() => {
                setSearchOpen(true);
                setTimeout(() => {
                  try { searchInputRef.current?.focus(); } catch (err) { /* noop */ }
                }, 0);
              }}
            >
              <Search size={13} />
              <Input
                className="w-[36ch] bg-transparent p-0 text-sm text-slate-700 placeholder:text-slate-400"
                placeholder="Search incidents, vehicles, or patient IDs..."
                value={query}
                onChange={(e) => {
                  setQuery(e.target?.value ?? "");
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                onKeyDown={handleInputKeyDown}
                ref={searchInputRef}
              />
              <span className="ml-2 inline-flex items-center gap-1 rounded border border-stone-200 px-1.5 text-[10px] text-slate-500">
                <CmdIcon size={10} /> K
              </span>
            </div>
          </PopoverAnchor>

          <PopoverContent className="p-0 mt-1 rounded-2xl shadow-2xl border-slate-200" align="start" style={{ width: searchRef.current?.offsetWidth || undefined }}>
            <div className="w-full">
              {searchLoading && (
                <div className="flex items-center justify-center gap-2 py-4 text-sm text-slate-400">
                  <Loader2 className="animate-spin" size={14} /> Searching…
                </div>
              )}

              {!searchLoading && searchError && query.trim().length >= 2 && (
                <div className="p-4 text-sm text-rose-600">{searchError}</div>
              )}

              {!searchLoading && !searchError && query.trim().length < 2 && (
                <div className="p-4 text-sm text-slate-400">Type at least 2 characters to search.</div>
              )}

              {!searchLoading && !searchError && query.trim().length >= 2 && flatItems.groups.length === 0 && (
                <div className="p-4 text-sm text-slate-400">No results found.</div>
              )}

              {!searchLoading && !searchError && flatItems.groups.map((g, gi) => (
                <div key={g.title} className="border-b last:border-b-0">
                  <div className="px-4 py-2 text-[11px] font-black text-slate-500 uppercase tracking-wider">{g.title}</div>
                  <div>
                    {g.items.map((it, ii) => {
                      const flatIndex = flatItems.flat.findIndex((f) => f.id === it.id && f.__group === g.title);
                      const title = it.title || it.label || it.display || "Untitled";
                      const subtitle = it.subtitle || null;
                      const matched = it.matched_value && it.matched_field;
                      const highlight = (text) => {
                        if (!matched) return text;
                        try {
                          const mv = String(it.matched_value);
                          const idx = String(text).toLowerCase().indexOf(mv.toLowerCase());
                          if (idx === -1) return text;
                          return (
                            <span>
                              {String(text).slice(0, idx)}<b className="bg-yellow-100">{String(text).slice(idx, idx + mv.length)}</b>{String(text).slice(idx + mv.length)}
                            </span>
                          );
                        } catch (e) {
                          return text;
                        }
                      };

                      return (
                        <div
                          key={`${g.title}-${it.id || ii}`}
                          ref={(el) => (itemRefs.current[flatIndex] = el)}
                          onMouseEnter={() => setActiveIndex(flatIndex)}
                          onClick={() => {
                            const resolved = resolveSearchNavigation(it.navigation, searchDashboard);
                            navigate(resolved.path, resolved.state ? { state: resolved.state } : undefined);
                            setSearchOpen(false);
                          }}
                          className={`px-4 py-3 cursor-pointer hover:bg-slate-50 ${activeIndex === flatIndex ? "bg-slate-50" : ""}`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-slate-900 truncate">{highlight(title)}</div>
                              {subtitle && <div className="text-[12px] text-slate-500 truncate">{highlight(subtitle)}</div>}
                            </div>
                            {Array.isArray(it.badges) && it.badges.length > 0 ? (
                              <div className="text-[11px] font-bold text-slate-400">{it.badges[0].label}</div>
                            ) : it.badge ? (
                              <div className="text-[11px] font-bold text-slate-400">{it.badge}</div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        <Popover open={bellOpen} onOpenChange={setBellOpen}>
          <PopoverTrigger asChild>
            <button
              data-testid="notifications-btn"
              className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-stone-200 bg-white text-slate-600 transition hover:bg-stone-100"
            >
              <Bell size={15} strokeWidth={1.75} />
              {totalUnreadCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white ring-2 ring-white">
                  {totalUnreadCount > 99 ? "99+" : totalUnreadCount}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[380px] p-0 rounded-2xl shadow-2xl border-slate-200" align="end">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/50">
              <div>
                <h4 className="text-sm font-black text-slate-900">Notifications</h4>
                <p className="text-[11px] text-slate-500 font-medium">
                  {totalUnreadCount > 0 ? `${totalUnreadCount} unread` : "All caught up"}
                </p>
              </div>
              <button
                onClick={markAllRead}
                disabled={unreadCount === 0}
                className="text-[11px] font-bold text-blue-600 hover:underline flex items-center gap-1"
              >
                <Check size={12} /> Mark all as read
              </button>
            </div>
            <div className="max-h-[420px] overflow-y-auto">
              {notifsLoading && (
                <div className="flex items-center justify-center gap-2 py-10 text-xs text-slate-400">
                  <RefreshCw size={13} className="animate-spin" /> Loading…
                </div>
              )}
              {!notifsLoading && notifications.length === 0 && (
                <p className="py-10 text-center text-xs text-slate-400">No notifications yet.</p>
              )}
              {!notifsLoading && notifications.map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`px-5 py-4 flex gap-4 border-b border-slate-50 transition-colors hover:bg-slate-50/50 relative cursor-pointer ${!n.is_read ? "bg-blue-50/20" : ""}`}
                >
                  <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                    n.type === "alert" ? "bg-rose-50 text-rose-500" :
                    n.type === "user"  ? "bg-emerald-50 text-emerald-500" :
                    n.type === "team"  ? "bg-blue-50 text-blue-500" :
                    "bg-slate-100 text-slate-500"
                  }`}>
                    {n.type === "alert"  && <AlertCircle size={18} />}
                    {n.type === "user"   && <UserPlus size={18} />}
                    {n.type === "team"   && <Users size={18} />}
                    {n.type === "update" && <Activity size={18} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2">
                      <p className={`text-[13px] leading-snug ${!n.is_read ? "font-bold text-slate-900" : "font-medium text-slate-600"}`}>
                        {n.title}
                      </p>
                      <span className="text-[10px] font-black text-slate-400 uppercase whitespace-nowrap">{n.time}</span>
                    </div>
                    {notifSubtitle(n) && (
                      <p className="text-[11px] font-mono font-bold text-slate-500 mt-1 uppercase tracking-wider">
                        {notifSubtitle(n)}
                      </p>
                    )}
                    {n.body && n.body !== n.title && (
                      <p className="text-[11px] text-slate-400 mt-0.5">{n.body}</p>
                    )}
                  </div>
                  {!n.is_read && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-blue-500" />}
                </div>
              ))}
            </div>
            <div className="p-3 bg-slate-50/50 border-t border-slate-100">
              <Button 
                variant="ghost" 
                className="w-full text-xs font-bold text-slate-500 hover:text-slate-900 h-9"
                onClick={() => { navigate(roleRoutes.alerts); }}
              >
                View All Activity Logs
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </header>



      <VehicleProfileDialog 
        open={profileOpen} 
        onOpenChange={setProfileOpen} 
        vehiclePlate={selectedVehiclePlate} 
      />

      <Dialog open={passwordDialogOpen} onOpenChange={handlePasswordDialogChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound size={16} />
              Update Password
            </DialogTitle>
            <DialogDescription>
              {user?.must_change_password
                ? "Your account is using a temporary password. Please set a new one now."
                : "Change your account password."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-password">New password</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">Confirm password</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
              />
            </div>

            {passwordError && (
              <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">
                {passwordError}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => handlePasswordDialogChange(false)} disabled={passwordSubmitting}>
                Later
              </Button>
              <Button type="button" onClick={handleUpdatePassword} disabled={passwordSubmitting}>
                {passwordSubmitting ? <Loader2 size={14} className="animate-spin" /> : "Update Password"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
